import React, { FC, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { summaries } from "../../../apis"
import { getSelectedLanguage, lang } from "../../../constants/languages"

export type PlatformCompleteness = {
  complete: string[]
  missing: string[]
}

export type PlatformCompletenessMap = { [month: string]: PlatformCompleteness }

type PopoverPosition = { top: number; left: number; openUp: boolean; maxHeight: number }

const POPOVER_WIDTH = 360
const GAP = 6
// Three months of platform lists, when there is room for them.
const POPOVER_MAX_HEIGHT = 440
// Under this the box is too short to be worth reading, so it is allowed to overhang instead.
const MIN_POPOVER_HEIGHT = 180
// Older months are long since fully reported, so only the tail of the list can still be incomplete.
const RECENT_MONTH_COUNT = 3

// A month is reported two months after the activity it covers, so activity 202605 is paid out on
// reporting period 202607.
const REPORTING_PERIOD_LAG = 2

// The months whose royalty figures are recent enough to be worth an info icon, oldest first.
export const recentMonths = (months: string[], count = RECENT_MONTH_COUNT) => [...months].sort().slice(-count)

const addMonths = (key: string, offset: number) => {
  const year = parseInt(key.slice(0, 4), 10)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(year) || isNaN(month)) return key
  const shifted = new Date(year, month - 1 + offset, 1)
  return `${shifted.getFullYear()}${String(shifted.getMonth() + 1).padStart(2, "0")}`
}

// The reporting periods still worth chasing: this month and the two before it, oldest first. Which
// report has landed and which has not is a question about the reporting calendar, so this window is
// the same whichever period the host screen happens to display its figures in.
export const latestReportingMonths = (count = RECENT_MONTH_COUNT) => {
  const now = new Date()
  const thisMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`
  return Array.from({ length: count }, (_, index) => addMonths(thisMonth, index - (count - 1)))
}

const monthYear = (key: string) => {
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(month)) return key
  const date = new Date(parseInt(key.slice(0, 4), 10), month - 1, 1)
  return date.toLocaleDateString(getSelectedLanguage(), { month: "short", year: "numeric" })
}

// Resolved (and in flight) requests keyed by user + the months they cover. A set of months is only
// fetched once, and only after the icon is clicked, so a page full of rows costs nothing until
// someone asks.
const completenessCache = new Map<string, Promise<PlatformCompletenessMap>>()

const fetchCompleteness = (userId: number | string, months: string[]) => {
  const monthsParam = months.join(",")
  const key = `${userId}_${monthsParam}`
  const cached = completenessCache.get(key)
  if (cached) {
    return cached
  }

  const request = summaries(`/platform_completeness/?user_id=${userId}&months=${monthsParam}`, "get")
    .then((resp) => (resp.data.results || {}) as PlatformCompletenessMap)
    .catch(() => {
      // Drop the failed request so the next click retries instead of serving an empty box forever.
      completenessCache.delete(key)
      return {} as PlatformCompletenessMap
    })

  completenessCache.set(key, request)
  return request
}

const platformsText = (platforms: string[] | undefined) =>
  platforms && platforms.length > 0 ? platforms.join(", ") : "-"

const PlatformList: FC<{ title: string; platforms: string[] | undefined }> = ({ title, platforms }) => (
  <div className="mt-1">
    <KSpan fontSize={13} color="#111" fontWeight={600} text={title} />
    <div>
      <KSpan fontSize={13} text={platformsText(platforms)} />
    </div>
  </div>
)

// Both lists of one month, kept together. Grouping by month rather than by list means a month is
// never split across a scroll, so "which platforms are still missing for August" is answered in one
// place instead of at the bottom of a second block.
const MonthBlock: FC<{ label: string; completeness: PlatformCompleteness | undefined }> = ({
  label,
  completeness
}) => (
  <div>
    <KTitleSpan fontSize={14} lineHeight="18px" text={label} />
    <PlatformList title={lang.royalties.upcoming.widget.complete_platform_list} platforms={completeness?.complete} />
    <PlatformList title={lang.royalties.upcoming.widget.missing_platform_list} platforms={completeness?.missing} />
  </div>
)

// Info icon next to a monthly royalty figure, or next to a row that stands for several months.
// Clicking it fetches and opens the complete/missing platform lists; KTooltip is hover only, so
// the popover is handled here.
const PlatformCompletenessInfo: FC<{
  userId: number | string | undefined
  // Rendered in the order given, so callers decide whether newest or oldest comes first.
  months: string[]
  // The endpoint always keys its platform lists by activity period. Screens showing reporting
  // periods must say so, otherwise every month looks unreported because its activity sits two
  // months earlier.
  reportingPeriod?: boolean
  // Heading for the single month case; defaults to the raw YYYYMM.
  label?: string
}> = ({ userId, months, reportingPeriod, label }) => {
  const [position, setPosition] = useState<PopoverPosition>()
  const [completenessMap, setCompletenessMap] = useState<PlatformCompletenessMap>()
  const [loading, setLoading] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const mountedRef = useRef(true)

  const open = position !== undefined
  const activityMonth = (month: string) => (reportingPeriod ? addMonths(month, -REPORTING_PERIOD_LAG) : month)
  // What the currently held map answers. A period switch keeps this component mounted (the month
  // label it hangs off can exist in both conventions), so without this the map would still be keyed
  // by the previous convention's activity months and every list would read as empty.
  const answersFor = `${userId}_${months.join(",")}_${reportingPeriod ? "reporting" : "activity"}`
  const answeredForRef = useRef(answersFor)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    answeredForRef.current = answersFor
    setCompletenessMap(undefined)
    setLoading(false)
  }, [answersFor])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setPosition(undefined)
      }
    }

    // The popover is anchored to viewport coordinates taken when it opened, so any scroll or resize
    // would leave it floating away from its icon. Closing is less surprising than a stale position.
    // Its own inner scrolling moves nothing, and the listener is capturing, so those events - which
    // would otherwise shut the box the moment the reader scrolled to the second month - are skipped.
    const close = (event?: Event) => {
      const target = event?.target
      if (target instanceof Node && popoverRef.current?.contains(target)) {
        return
      }
      setPosition(undefined)
    }

    document.addEventListener("mousedown", handleClickOutside)
    window.addEventListener("scroll", close, true)
    window.addEventListener("resize", close)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      window.removeEventListener("scroll", close, true)
      window.removeEventListener("resize", close)
    }
  }, [open])

  if (!userId || months.length === 0) {
    return null
  }

  const togglePopover = () => {
    if (open) {
      setPosition(undefined)
      return
    }

    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }

    // Keep the box inside the viewport on both axes: nudge it left when it would run off the right
    // edge, open it towards whichever side has more room, and cap its height to that room so the
    // last month is reachable by scrolling inside the box rather than lost under the fold.
    const left = Math.max(GAP, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - GAP))
    const spaceBelow = window.innerHeight - rect.bottom - GAP * 2
    const spaceAbove = rect.top - GAP * 2
    const openUp = spaceAbove > spaceBelow
    const maxHeight = Math.min(POPOVER_MAX_HEIGHT, Math.max(openUp ? spaceAbove : spaceBelow, MIN_POPOVER_HEIGHT))

    setPosition({ left, top: openUp ? rect.top - GAP : rect.bottom + GAP, openUp, maxHeight })

    if (completenessMap) {
      return
    }

    setLoading(true)
    // The endpoint takes every month in one call, so the whole popover costs a single request. It
    // answers in activity periods, so the result is keyed back to the months on screen.
    const requestedFor = answersFor
    fetchCompleteness(userId, months.map(activityMonth)).then((result) => {
      // Anything that arrives for a period the screen has since left would be read with the wrong
      // month keys, so it is dropped rather than shown.
      if (!mountedRef.current || answeredForRef.current !== requestedFor) {
        return
      }
      setCompletenessMap(result)
      setLoading(false)
    })
  }

  return (
    <div className="inline-flex items-center flex-shrink-0">
      <div
        ref={buttonRef}
        className="cursor-pointer flex items-center flex-shrink-0"
        title={months.length === 1 ? `${label || months[0]} platform status` : "Platform reporting status"}
        onClick={togglePopover}
      >
        {/* Tailwind's preflight gives images `max-width: 100%; height: auto`, so inside a squeezed
            table cell the icon would shrink to nothing. The explicit box keeps it at 14px. */}
        <img
          src="/navigation_icons/help.svg"
          alt=""
          style={{ width: 14, height: 14, minWidth: 14, minHeight: 14, maxWidth: "none" }}
        />
      </div>
      {position &&
        createPortal(
          <div
            ref={popoverRef}
            className="p-3 bg-white flex flex-col gap-2"
            style={{
              // Rendered in a portal because the host tables live inside overflow containers, which
              // would otherwise clip the box on the lower rows.
              position: "fixed",
              top: position.top,
              left: position.left,
              transform: position.openUp ? "translateY(-100%)" : undefined,
              zIndex: 1000,
              width: POPOVER_WIDTH,
              maxHeight: position.maxHeight,
              overflowY: "auto",
              // Without this the wheel keeps travelling to the page once the box hits its end, and
              // that page scroll closes the box mid-read.
              overscrollBehavior: "contain",
              border: "1px solid #11111120",
              borderRadius: 5,
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
              // Table cells set nowrap / right alignment, and the portal must not inherit whatever
              // the host cell happened to use either.
              whiteSpace: "normal",
              textAlign: "left",
              wordBreak: "break-word"
            }}
          >
            {loading ? (
              <div className="animate-pulse flex flex-col gap-2">
                <div className="h-3 w-32 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded" />
                <div className="h-3 w-4/5 bg-gray-200 rounded" />
              </div>
            ) : (
              months.map((month, index) => (
                <React.Fragment key={`completeness_${month}`}>
                  {index > 0 && <div style={{ borderTop: "1px dashed #11111120" }} />}
                  <MonthBlock
                    label={months.length === 1 ? label || month : monthYear(month)}
                    completeness={completenessMap?.[activityMonth(month)]}
                  />
                </React.Fragment>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  )
}

export default PlatformCompletenessInfo
